import axios, { AxiosResponse } from "axios";
import { QuoteTypeEnum, UserRole } from "../lib/models/enums";
import { QuoteResponse } from "../lib/models/quote-response.model";
import { Quote } from "../lib/models/quote.model";
import { GetQuotesRequest } from "../lib/requests/quote-requests";
import { Comment } from "../lib/types/CommentType";
import { MetricType } from "../lib/types/MetricType";
import { QuoteType } from "../lib/types/QuoteType";
import { UserAccount } from "../lib/types/UserAccountType";
import { ApiUtils } from "./ApiUtils";

export type MetricRequest = {
  env: string;
  username: string;
  producttype: QuoteType;
  quotetype: MetricType;
  quoteid: number | undefined;
  quoteloanamount: string;
};

export class QuoteApi {
  private static readonly _baseUrl = ApiUtils.getBaseApiUrl();

  public static createQuote(quote: any): Promise<AxiosResponse<any>> {
    return axios.post(`${this._baseUrl}/quotes`, quote);
  }

  public static getQuotes(
    request: GetQuotesRequest
  ): Promise<AxiosResponse<Quote[]>> {
    return axios.get(
      `${this._baseUrl}/quoteSummaries?username=${request.username}&allquotes=${request.allquotes}`
    );
  }

  public static getQuotesById(id: string): Promise<AxiosResponse<Quote[]>> {
    return axios.get(`${this._baseUrl}/quotes/users/${id}`);
  }

  public static getQuoteUsers(): Promise<AxiosResponse<UserAccount[]>> {
    return axios.get(`${this._baseUrl}/users`);
  }

  public static getQuotesForReview(
    id: string
  ): Promise<AxiosResponse<Quote[]>> {
    return axios.get(`${this._baseUrl}/quotes/brokers`, {
      params: {
        id,
      },
    });
  }

  public static getQuotesForReviewById(
    id: string
  ): Promise<AxiosResponse<Quote[]>> {
    return axios.get(`${this._baseUrl}/quotes/brokers/${id}`, {
      params: {
        id,
      },
    });
  }

  public static getQuoteById(
    id: number
  ): Promise<AxiosResponse<QuoteResponse>> {
    return axios.get(`${this._baseUrl}/quotes/${id}`);
  }

  public static getBrokerQuoteById(
    id: string
  ): Promise<AxiosResponse<Quote[]>> {
    return axios.get(`${this._baseUrl}/quotes/brokers/${id}`);
  }

  public static getBorrowerQuoteById(
    id: string
  ): Promise<AxiosResponse<Quote[]>> {
    return axios.get(`${this._baseUrl}/quotes/borrowers/${id}`);
  }

  public static updateQuote(
    id: number,
    quote: any
  ): Promise<AxiosResponse<any>> {
    return axios.put<any>(`${this._baseUrl}/quotes/${id}`, quote);
  }

  public static deactivateQuote(id: number) {
    return axios.delete<any>(`${this._baseUrl}/quotes/${id}`);
  }

  public static createQuoteComment(
    id: number,
    text: string,
    username: string
  ): Promise<AxiosResponse<Comment>> {
    return axios.post<any>(`${this._baseUrl}/quotes/${id}/comments`, {
      text,
      username,
    });
  }

  public static deactivateComment(quoteId: number, commentId: number) {
    return axios.delete<any>(
      `${this._baseUrl}/quotes/${quoteId}/comments/${commentId}`
    );
  }

  public static getQuoteComments(
    id: number
  ): Promise<AxiosResponse<Comment[]>> {
    return axios.get<any>(`${this._baseUrl}/quotes/${id}/comments`);
  }

  public static submitQuoteToSalesforce(quote: any) {
    return axios.post(`${this._baseUrl}/submitquote`, quote);
  }

  public static submitQuoteForReview(quote: any) {
    return axios.post(`${this._baseUrl}/submitquoteforreview`, quote);
  }

  public static submitQuoteExceptions(
    id: number,
    status: string | undefined,
    reduction?: number | undefined
  ) {
    return axios.post(`${this._baseUrl}/quotes/${id}/exceptions`, {
      status,
      reduction,
    });
  }

  public static submitQuoteChoices(id: number) {
    return axios.put(`${this._baseUrl}/quotes/${id}/finalize`, {});
  }

  public static getAutoCommission(
    role: UserRole,
    fee: number,
    rate: number,
    productType: QuoteTypeEnum,
    loanProduct: string
  ) {
    return axios.get(
      `${this._baseUrl}/commission?role=${role}&quotetype=${productType}&rate=${rate}&fee=${fee}&product=${loanProduct}`
    );
  }

  public static saveMetric(request: MetricRequest): Promise<any> {
    return axios.post(`${this._baseUrl}/Metrics`, request);
  }

  public static submitSalesFinalized(id: number) {
    return axios.put(`${this._baseUrl}/quotes/${id}/finalize`);
  }

  public static getWholesalePartners(): Promise<
    AxiosResponse<{ id: number; wholesalePartner: string }[]>
  > {
    return axios.get<any>(`${this._baseUrl}/wholesale-partner`);
  }
}
